import Outpoint from '@/utils/outpoint'
import { NETWORK_FUNGIBLE_PROTOCOL } from '@/utils/constants'

/**
 * Token amount on a BRC-20/TAP transfer inscription.
 *
 * Turbo sometimes tags `brc20-*` transfers with `meta: null` (issue #568).
 * Reading `meta.attributes[0]` throws and takes down the Sell tab. Prefer the
 * meta attribute, then `brc20.amount`, then a transfer body already on the
 * row (`content` / `body`). Returns null when none of those is a positive
 * amount so callers can skip the row instead of inventing one.
 */
export function fungibleInscriptionAmount(inscription: any): string | number | null {
	const fromMeta = readAmount(inscription?.meta?.attributes?.[0]?.value)
	if (fromMeta != null) return fromMeta

	const fromBrc20 = readAmount(inscription?.brc20?.amount)
	if (fromBrc20 != null) return fromBrc20

	return readAmount(contentAmount(inscription))
}

function readAmount(value: unknown): string | number | null {
	if (typeof value === 'number') {
		return Number.isFinite(value) && value > 0 ? value : null
	}
	if (typeof value !== 'string') return null
	const trimmed = value.trim()
	if (!trimmed) return null
	const parsed = Number(trimmed)
	return Number.isFinite(parsed) && parsed > 0 ? trimmed : null
}

function contentAmount(inscription: any): unknown {
	const content = inscription?.content ?? inscription?.body
	if (content == null) return null

	let parsed: unknown = content
	if (typeof content === 'string') {
		const trimmed = content.trim()
		if (!trimmed.startsWith('{')) return null
		try {
			parsed = JSON.parse(trimmed)
		} catch {
			return null
		}
	}

	if (!parsed || typeof parsed !== 'object') return null
	const record = parsed as { amt?: unknown; amount?: unknown }
	return record.amt ?? record.amount
}

/** Genesis outpoint check used to ignore already-spent transfer inscriptions. */
export function isValidTransferOutpoint(inscription: any): boolean {
	try {
		const encoded = inscription?.outpoint?.outpoint
		const id = inscription?.id
		if (typeof encoded !== 'string' || typeof id !== 'string') return false
		return Outpoint.decode(encoded).txid === id.split('i')[0]
	} catch {
		return false
	}
}

/**
 * Legacy BRC-20 manager rows. Same collection + valid-outpoint filter as
 * `brc20-list`, minus rows whose amount cannot be read.
 */
export function selectBrc20ListTransfers(
	inscriptions: any[] | null | undefined,
	options: { ticker: string; slug?: string }
): any[] {
	const { ticker, slug } = options

	return (inscriptions || [])
		.filter((e) => {
			if (slug) return e?.collection?.slug === slug
			const protocolSlug = `${NETWORK_FUNGIBLE_PROTOCOL.replace('-', '')}-${ticker.replace(
				'.',
				'dot'
			)}`
			return e?.collection?.slug === protocolSlug || e?.collection?.slug === `tap-${ticker}`
		})
		.filter((e) => isValidTransferOutpoint(e))
		.flatMap((e) => {
			const amount = fungibleInscriptionAmount(e)
			return amount == null ? [] : [{ ...e, amount }]
		})
}
